import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';

import { Control, RegisterOptions, useController } from 'react-hook-form';
import { useFieldStateError } from './useFieldStateError';
import { useCallback, useMemo } from 'react';
import { formatDate } from '@razz-team/utils';

export type DateFieldProps = Omit<
  DatePickerProps,
  'name' | 'value' | 'required' | 'disabled' | 'onBlur' | 'onChange'
> & {
  name: string;
  control?: Control;
} & Pick<RegisterOptions, 'required' | 'disabled' | 'validate'>;

export function DateField({ name, control, required, ...props }: DateFieldProps) {
  const { field, fieldState } = useController({
    name,
    control,
    disabled: props.disabled,
    rules: {
      required,
    },
  });

  const fieldError = useFieldStateError(props.label, fieldState.error);

  const dateValue = useMemo(
    () =>
      field.value ? (typeof field.value === 'string' ? new Date(field.value) : field.value) : null,
    [field.value],
  );

  const handleChange = useCallback(
    (newValue: Date | null) => {
      field.onChange(formatDate(newValue, 'yyyy-MM-dd') ?? null);
    },
    [field],
  );

  return (
    <DatePicker
      {...props}
      name={field.name}
      value={dateValue}
      format="dd/MM/yyyy"
      onChange={handleChange}
      disabled={field.disabled}
      enableAccessibleFieldDOMStructure={true}
      slotProps={{
        textField: {
          onBlur: field.onBlur,
          required: Boolean(required),
          error: fieldState.invalid,
          helperText: fieldError,
        },
      }}
    />
  );
}
